import { useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";

// Typed-name confirmation for the irreversible company delete. Backend snapshots before cascading.
export function DeleteCompanyDialog({ company, postedRuns, onClose, onDeleted }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const name = company?.name || "";
  const ok = typed.trim() === name.trim() && !busy;

  const run = async () => {
    setBusy(true);
    try {
      await api.delete(`/companies/${company.company_id}`, { params: { confirm_name: typed.trim() } });
      toast.success(`${name} deleted`);
      onDeleted();
    } catch (e) { toast.error(e?.response?.data?.detail || apiErr(e)); } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="delete-company-dialog" onClick={onClose}>
      <div className="bg-white border border-red-600 w-full max-w-md p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-bold tracking-tight flex items-center gap-2 text-red-700"><AlertTriangle className="h-4 w-4" /> Delete {name}</h3>
          <button onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-black"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-sm text-muted-foreground">
          This removes the company, its employees, client logins, time, payroll history and documents from the app.
          {postedRuns > 0 && <strong className="text-red-700"> {postedRuns} posted payroll run{postedRuns === 1 ? "" : "s"} will be removed.</strong>}
          {" "}A recovery snapshot is kept by Postings Inc.
        </p>
        <label className="block">
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Type the company name to confirm</span>
          <input data-testid="delete-company-confirm-input" autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={name}
            className="mt-1 w-full border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-600" />
        </label>
        <div className="flex gap-2">
          <button data-testid="delete-company-confirm-button" onClick={run} disabled={!ok}
            className="flex-1 bg-red-600 text-white px-4 py-2.5 text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-40">{busy ? "Deleting…" : "Delete permanently"}</button>
          <button onClick={onClose} className="border border-border px-4 py-2.5 text-sm hover:border-black transition-colors">Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
